import { storeToRefs } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';

import { useCurrentAccountStore } from '@abaninja/data-access-swiss21-account';
import { type Account, type Me, useMeStore, useUserRoleStore } from '@abaninja/data-access-swiss21-users';
import { useAccountRoleStore, useAccountsStore } from '@abaninja/data-access-swiss21-users';
import { useRouterState } from '@abaninja/util-router';

import { switchLanguage } from '../plugins/i18n';
import { routes } from './routes';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// eslint-disable-next-line complexity, max-lines-per-function
router.beforeEach(async (to) => {
  const { isLoading } = storeToRefs(useRouterState());

  isLoading.value = true;

  // load accounts
  const { entities, requireEntities } = useAccountsStore();

  // load me
  const meStore = useMeStore();
  const { entity } = storeToRefs(meStore);

  try {
    await Promise.all([requireEntities(), meStore.requireEntity()]);
  } catch {
    return false;
  }

  const { is: isUserRole, needs: needsUserRole, setCurrentLevel: setUserRole } = useUserRoleStore();

  if (entity.value) {
    // set account Role

    setUserRole(entity.value.userRole);

    await switchLanguage(entity.value.language as SupportedLocale);
  }

  if (to.meta.needsAccount && !entities.value.length) {
    if (isUserRole('SUPPORT')) {
      return { name: 'support-granted-accounts' };
    }

    // no account present, so redirect to onboarding
    return { name: 'onboarding' };
  }

  const { needs: needsAccountRole, setCurrentLevel: setAccountRole } = useAccountRoleStore();

  if (to.meta.needsAccount) {
    const currentAccountStore = useCurrentAccountStore();
    const { accountRole, entity: currentAccount } = storeToRefs(currentAccountStore);

    try {
      await currentAccountStore.setDefaultAccount(to);
    } catch {
      return false;
    }

    // set account Role
    setAccountRole(accountRole.value);

    // check if needs redirect to accept agb page
    if (
      !['accept-agb', 'accounts', 'create-account'].includes(to.name?.toString() ?? '')
      && needsAccountRole('OWNER')
      && currentAccount.value
      && (!currentAccount.value.acceptedAgb || !currentAccount.value.acceptedPrivacyPolicy)
    ) {
      return { name: 'accept-agb' };
    }
  }

  if (to.meta.permissions) {
    if (!(to.meta.permissions as Account['accountRole'][]).some(needsAccountRole)) {
      // no access
      return { name: 'dashboard' };
    }
  }

  if (to.meta.userRoles) {
    if (!(to.meta.userRoles as Me['userRole'][]).some(needsUserRole)) {
      // no access
      return { name: 'dashboard' };
    }
  }

  if (to.meta.isUserRoleOr) {
    if (!(to.meta.isUserRoleOr as Me['userRole'][]).some(isUserRole)) {
      // no access
      return { name: 'dashboard' };
    }
  }

  return true;
});

router.afterEach(() => {
  const { hasLoaded, isLoading } = storeToRefs(useRouterState());

  isLoading.value = false;
  hasLoaded.value = true;
});

export default router;
